import { EventDetails, LinkedTG } from '@/api/Helpers'
import type { Session } from '@/api/Sessions'
import QueryParameterError from '@/errors/QueryParameterError'
import { getQuantityParam, getQueryParam, NormalizedReserveParamValue } from '@/helpers/QueryStringParameters'
import { isTrueish } from '@/helpers/StringHelpers'
import moment, { Moment } from 'moment'
import { Vue } from 'vue-property-decorator'

export default abstract class ReserveBase extends Vue {
  abstract event: EventDetails

  abstract availableDates: string[]
  abstract selectedDate: Moment | null
  abstract sessions: Session[] | null
  abstract selectedSession: Session | null

  abstract selectedQuantities: TicketTypeQuantities

  abstract availableTGs: LinkedTG[]

  initializationWarning: string | null = null
  initializationError: string | null = null

  get availableTicketTypes(): TicketType[] {
    return this.availableTGs.reduce((tickeTypes: TicketType[], group) => {
      return [...tickeTypes, ...group.types]
    }, [])
  }

  selectDate(date: Moment): SuccessOrFailureReason {
    // Check if the date of the session is still available before selecting it
    if (this.availableDates.includes(date.format('YYYY-MM-DD'))) {
      this.selectedDate = date
      return ''
    }

    return this.$t('reserve.unavailableOnDate') as string
  }

  selectDateBySession(session: EventSession, venue: Venue): SuccessOrFailureReason {
    const date = moment(session.start_datetime).tz(venue.timezone)
    return this.selectDate(date)
  }

  selectSessionByID(sessionID: string): SuccessOrFailureReason {
    const session = this.sessions?.find((s) => s.id === sessionID)
    if (session) {
      this.selectedSession = session
      return ''
    }
    return this.$t('reserve.sessionUnavailable') as string
  }

  selectQuantitiesByQueryParams(): SuccessOrFailureReason {
    const quantityParamValue = getQuantityParam()?.value
    if (!quantityParamValue) {
      return ''
    }

    const tempQuantities: TicketTypeQuantities = {}
    for (const [ticketType, num] of Object.entries(quantityParamValue)) {
      const types = this.availableTicketTypes.filter((t) => {
        // Ticket type can be id or name
        return t.id === ticketType || t.name === ticketType
      })

      for (const type of types) {
        tempQuantities[type.id] = { quantity: num }
      }
    }

    if (Object.keys(tempQuantities).length === 0) {
      return this.$tc('reserve.ticketTypesNotFound', Object.keys(quantityParamValue).length) as string
    }

    this.selectedQuantities = { ...this.selectedQuantities, ...tempQuantities }
    return ''
  }

  normalizeReserveParam(): NormalizedReserveParamValue {
    const reserveParam = getQueryParam('reserve')?.value ?? ''
    let reserve: NormalizedReserveParamValue = 'false'

    if (['warn', 'strict'].includes(reserveParam)) {
      reserve = reserveParam as NormalizedReserveParamValue
    } else if (isTrueish(reserveParam)) {
      reserve = 'true'
    }

    return reserve
  }

  handleQueryParamError(error: string): void {
    if (error) {
      throw new QueryParameterError(error)
    }
  }

  handleLoadParamError(e: QueryParameterError, reserve: NormalizedReserveParamValue) {
    if (!e.isQueryParameterError) {
      throw e
    } else if (reserve === 'warn') {
      this.initializationWarning = e.message
    } else if (reserve === 'strict') {
      this.initializationError = this.$t('reserve.unavailable') as string
    }
  }
}
